import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Heart, Users, Check } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

import heroImg from "../assets/hero.png";
import mirrorImg from "../assets/mirror.png";


export default function Home({ settings }) {
  const { t, lang } = useLang();
  const [faqs, setFaqs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const suffix = lang === "hi" ? "Hi" : lang === "ta" ? "Ta" : "";
  const pick = (obj, base) => obj[base + suffix] || obj[base];

  useEffect(() => {
    api.get("/api/faqs").then((r) => setFaqs(r.data.slice(0, 5))).catch(() => {});
    api.get("/api/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const why = [
    { Icon: Compass, title: t("why.c1.t"), desc: t("why.c1.d") },
    { Icon: Heart, title: t("why.c2.t"), desc: t("why.c2.d") },
    { Icon: Users, title: t("why.c3.t"), desc: t("why.c3.d") },
  ];

  return (
    <div data-testid="home-page">
      <section className="hero-grain">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:py-24">
          <div className="fade-up">
            <span className="inline-block rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground" data-testid="hero-badge">
              {t("hero.badge")}
            </span>
            <h1 className="font-display mt-5 text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {(settings && pick(settings, "heroTitle")) || "Discover the real you"}
            </h1>
            <p className="mt-5 text-base text-muted-foreground md:text-lg" data-testid="hero-subtitle">
              {(settings && pick(settings, "heroSubtitle")) || ""}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup">
                <Button size="lg" className="rounded-full px-6" data-testid="hero-take-test">{t("hero.cta.test")}<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </Link>
              <Link to="/types">
                <Button size="lg" variant="outline" className="rounded-full px-6" data-testid="hero-explore-types">{t("hero.cta.types")}</Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 sm:gap-10" data-testid="hero-stats">
              {[["16", "stats.archetypes"], ["150", "stats.statements"], ["~20", "stats.minutes"]].map(([n, k]) => (
                <div key={k}>
                  <div className="font-display text-3xl font-semibold text-primary">{n}</div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t(k)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="fade-up fade-up-1 hidden md:block">
            {settings?.showHeroImage !== false && (
              <img src={heroImg} alt="EdviaIQ student" className="aspect-[4/3] w-full rounded-3xl object-cover shadow-xl" data-testid="hero-image" />
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="why-title">{t("why.title")}</h2>
        <p className="mt-3 text-base text-muted-foreground md:text-lg">{t("why.sub")}</p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {why.map(({ Icon, title, desc }, i) => (
            <div key={i} className="rounded-2xl border border-border bg-card p-7 transition-shadow hover:shadow-lg" data-testid={`why-card-${i}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><Icon className="h-5 w-5" /></span>
              <h3 className="font-display mt-4 text-xl font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-card py-16">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 md:grid-cols-2">
          <img src={mirrorImg} alt="Students discovering themselves" className="aspect-[5/4] w-full rounded-3xl object-cover shadow-lg" data-testid="mirror-image" />
          <div>
            <h2 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="mirror-title">{t("mirror.title")}</h2>
            <p className="mt-3 text-base text-muted-foreground md:text-lg">{t("mirror.sub")}</p>
            <ul className="mt-6 space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <li key={i} className="flex items-center gap-3 text-sm font-medium" data-testid={`mirror-point-${i}`}>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary text-primary"><Check className="h-3.5 w-3.5" /></span>
                  {t(`mirror.${i}`)}
                </li>
              ))}
            </ul>
            <Link to="/signup">
              <Button className="mt-8 rounded-full px-6" data-testid="mirror-cta">{t("mirror.cta")}</Button>
            </Link>
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-16">
          <h2 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="testi-title">{t("testi.title")}</h2>
          <p className="mt-3 text-base text-muted-foreground md:text-lg">{t("testi.sub")}</p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {testimonials.map((tm) => (
              <figure key={tm.id} className="rounded-2xl border border-border bg-card p-7" data-testid={`testimonial-${tm.id}`}>
                <blockquote className="text-sm leading-relaxed text-foreground">“{pick(tm, "quote")}”</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary font-semibold text-primary">
                    {(tm.name || "?")[0]}
                  </span>
                  <div>
                    <div className="text-sm font-semibold">{tm.name}</div>
                    <div className="text-xs text-muted-foreground">{pick(tm, "role")}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 pb-20">
          <h2 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="faq-title">{t("faq.title")}</h2>
          <p className="mt-3 text-base text-muted-foreground md:text-lg">{t("faq.sub")}</p>
          <Accordion type="single" collapsible className="mt-8" data-testid="home-faq-list">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} data-testid={`faq-item-${f.id}`}>
                <AccordionTrigger className="text-left text-sm font-semibold">{pick(f, "q")}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{pick(f, "a")}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}
    </div>
  );
}
